import type { NPSCampaign } from '@/api/surveys/getNPSCampaignQuery';
import { useLaunchDarklyFlag } from '@/hooks/flags/useLaunchDarklyFlag';
import { useCustomerStateQuery } from '@/hooks/queries/customer/useCustomerStateQuery/useCustomerStateQuery';
import { useGetNPSCampaignQuery } from '@/hooks/queries/surveys/useGetNPSCampaignQuery';
import { Logger } from '@/utils/logging';

interface UseNPSSurveyEligibilityResult {
  campaign: NPSCampaign | null;
}

export function useNPSSurveyEligibility(
  customerId: string,
): UseNPSSurveyEligibilityResult {
  const minAppointments = useLaunchDarklyFlag<number>(
    'min-appointment-for-nps',
    2,
  );

  const { data: customerState } = useCustomerStateQuery({
    customerId,
    enabled: !!customerId,
  });

  const appointmentCount =
    (customerState?.upcomingAppointments?.length ?? 0) +
    (customerState?.pastAppointments?.length ?? 0);

  const isEligible =
    !!customerId && !!customerState && appointmentCount >= minAppointments;

  if (__DEV__) {
    Logger.console('[NPS] eligibility check', {
      customerId,
      minAppointments,
      appointmentCount,
      isEligible,
    });
  }

  const { data: campaignData } = useGetNPSCampaignQuery(customerId, {
    enabled: isEligible && !!customerId,
  });

  if (__DEV__) {
    Logger.console('[NPS] campaign result', {
      campaign: campaignData?.campaign ?? null,
    });
  }

  return {
    campaign: campaignData?.campaign ?? null,
  };
}
